import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROUTES } from 'config/navConfig';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import { MOCK_SPVS, MOCK_FUNDS } from 'data/mockData';

const STATUS_CONFIG = {
  active:          { label: 'Active',        color: '#059669', bg: 'rgba(5,150,105,0.1)'  },
  fundraising:     { label: 'Fundraising',   color: 'var(--color-primary)', bg: 'rgba(59,76,76,0.1)' },
  'pre-formation': { label: 'Pre-Formation', color: '#d97706', bg: 'rgba(217,119,6,0.1)'  },
};

function SPVCard({ spv }) {
  const navigate = useNavigate();
  const statusCfg = STATUS_CONFIG[spv.status] ?? STATUS_CONFIG['active'];
  const totalCalled = spv.commitments?.reduce((s, c) => s + (c.called ?? 0), 0) ?? 0;
  const fmtM = (n) => `£${(n / 1_000_000).toFixed(1)}m`;

  return (
    <div className="card p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-foreground text-sm leading-snug truncate">{spv.name}</p>
          {spv.assetSnapshot && (
            <p className="font-caption text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
              <Icon name="Building2" size={10} color="var(--color-muted-foreground)" />
              {spv.assetSnapshot.name} · {spv.assetSnapshot.geography}
            </p>
          )}
        </div>
        <span
          className="font-caption text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0"
          style={{ color: statusCfg.color, background: statusCfg.bg }}
        >
          {statusCfg.label}
        </span>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="font-caption text-xs text-muted-foreground">Target IRR</p>
          <p className="font-caption text-sm font-semibold text-foreground">{spv.targetIRR}</p>
        </div>
        <div>
          <p className="font-caption text-xs text-muted-foreground">Capital</p>
          <p className="font-caption text-sm font-semibold text-foreground truncate">
            {fmtM(spv.raisedAmount)} / {fmtM(spv.targetSize)}
          </p>
        </div>
        <div>
          <p className="font-caption text-xs text-muted-foreground">Investors</p>
          <p className="font-caption text-sm font-semibold text-foreground">{spv.investorCount}</p>
        </div>
      </div>

      {/* Capital call alert */}
      {spv.capitalCall?.status === 'issued' && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)' }}
        >
          <Icon name="AlertCircle" size={12} color="#d97706" />
          <p className="font-caption text-xs" style={{ color: '#d97706' }}>
            Call #{spv.capitalCall.callNumber} — {fmtM(spv.capitalCall.amount)} due {spv.capitalCall.dueDate}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-border">
        <button
          onClick={() => navigate(ROUTES.spvDashboard, { state: { spvId: spv.id } })}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg font-caption text-xs font-semibold transition-colors"
          style={{ background: 'rgba(59,76,76,0.07)', color: 'var(--color-primary)' }}
        >
          Open Dashboard
          <Icon name="ArrowRight" size={12} color="var(--color-primary)" />
        </button>
      </div>
    </div>
  );
}

export default function SPVDealsPage() {
  const navigate = useNavigate();
  const { role, activeContext } = useRole();
  const isCoInvestor = role === ROLES.CO_INVESTOR || role === ROLES.EXTERNAL_INVESTOR;
  const canCreateSPV = role === ROLES.INVESTMENT_TEAM || role === ROLES.PLATFORM_ADMIN;

  // Co-investor only sees their own committed SPVs
  const visibleSPVs = isCoInvestor
    ? MOCK_SPVS.filter((s) => s.commitments?.some((c) => c.contextId === activeContext?.id))
    : MOCK_SPVS;

  // Group by fund for internal roles
  const fundGroups = MOCK_FUNDS.map((fund) => ({
    fund,
    spvs: visibleSPVs.filter((s) => s.fundId === fund.id),
  })).filter((g) => g.spvs.length > 0);

  const totalRaisedForFund = (fund) => {
    const spvs = MOCK_SPVS.filter((s) => s.fundId === fund.id);
    return spvs.reduce((sum, s) => sum + (s.raisedAmount ?? 0), 0);
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Sticky header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 mb-2 pl-10 lg:pl-0" aria-label="Breadcrumb">
            <button
              onClick={() => navigate(ROUTES.dashboard)}
              className="font-caption text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Portfolio
            </button>
            <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
            <span className="font-caption text-xs text-foreground font-medium">SPVs &amp; Deals</span>
          </nav>
          <div className="flex items-center justify-between gap-4 pl-10 lg:pl-0">
            <div>
              <h1
                className="font-heading font-semibold text-foreground"
                style={{ fontSize: '1.25rem', lineHeight: 1.3 }}
              >
                SPVs &amp; Deals
              </h1>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">
                {isCoInvestor
                  ? 'Your co-investment positions'
                  : `${visibleSPVs.length} SPVs across ${MOCK_FUNDS.length} funds`}
              </p>
            </div>
            {canCreateSPV && (
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                iconSize={14}
                onClick={() => navigate(ROUTES.spvCreation)}
              >
                New SPV
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-8">
          {isCoInvestor ? (
            /* Co-investor: flat list under "Your Co-Investment" heading */
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Eye" size={15} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.95rem' }}>
                  Your Co-Investment
                </h2>
              </div>
              {visibleSPVs.length === 0 ? (
                <p className="font-caption text-sm text-muted-foreground">No active co-investment positions found.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {visibleSPVs.map((spv) => (
                    <SPVCard key={spv.id} spv={spv} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Internal roles: grouped by fund */
            fundGroups.map(({ fund, spvs }) => {
              const raised = totalRaisedForFund(fund);
              return (
                <div key={fund.id}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Layers" size={15} color="var(--color-primary)" />
                      <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '0.95rem' }}>
                        {fund.name}
                      </h2>
                    </div>
                    <span className="font-caption text-xs text-muted-foreground">
                      {spvs.length} SPV{spvs.length !== 1 ? 's' : ''} · £{(raised / 1_000_000).toFixed(0)}m raised
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {spvs.map((spv) => (
                      <SPVCard key={spv.id} spv={spv} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AppLayout>
  );
}
